import {announce} from '@react-aria/live-announcer'
import {isAfter} from 'date-fns'
import {klona} from 'klona'

import searchSvg from '@/assets/icons/search.svg'
import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import LoadingIcon from '@/components/LoadingIcon'
import ProductCard from '@/components/ProductCard/ProductCard'
import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import Pagination from '@/layout/Pagination'
import {getBookmarkObserveQueryOptions} from '@/lib/bookmark/hooks/useBookmark'
import generateHistoryBreakpoints from '@/lib/chat/generateHistoryBreakpoints'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import {type BookmarkedProduct} from '@/models/productModel'
import {BookmarkRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useWindowSize from '@/utils/hooks/useWindowSize'
import useSearch from '@/utils/router/useSearch'

import style from './Bookmark.module.scss'

const ITEMS_PER_PAGE = 20
const ITEMS_PER_PAGE_MOBILE = 10

function bookmarkedProductsChunkify(products: BookmarkedProduct[]) {
  let p = klona(products)
  if (p.length === 0) return []

  p.sort((a, b) => Number(b.creationTime) - Number(a.creationTime))

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guaranteed to exist
  const latestProduct = p[p.length - 1]!

  const breakpoints = generateHistoryBreakpoints(latestProduct.creationTime)

  const result = breakpoints.map(breakpoint => {
    const breakpointIndex = p.findLastIndex(product =>
      isAfter(product.creationTime, breakpoint.time),
    )
    let breakpointThreads: BookmarkedProduct[] = []

    if (breakpointIndex !== -1) {
      breakpointThreads = p.slice(0, breakpointIndex + 1)
      p = p.slice(breakpointIndex + 1)
    }

    return {
      ...breakpoint,
      products: breakpointThreads,
    }
  })

  return result.filter(r => r.products.length > 0)
}

export default memo(function Bookmark() {
  const router = useRouter()
  const [locale] = useLocale()
  const {isMobile} = useWindowSize()
  const accountInformation = useAccountInformationValue()

  const {
    data: bookmarkedProducts,
    isLoading,
    isFetching,
  } = useQuery(
    getBookmarkObserveQueryOptions({
      locale,
      accountInformation,
    }),
  )

  const [q, setQ] = useState('')
  const deferredQ = useDeferredValue(q)

  // TODO: fuzzy search
  const filteredBookmarkedProducts = useMemo<BookmarkedProduct[]>(() => {
    if (!bookmarkedProducts) return []
    return deferredQ
      ? Array.from(bookmarkedProducts.values()).filter(product => {
          return product.title.toLowerCase().includes(deferredQ.toLowerCase())
        })
      : Array.from(bookmarkedProducts.values())
  }, [bookmarkedProducts, deferredQ])

  const [page, setPage] = useSearch(BookmarkRoute.fullPath, 'page', {
    replace: false,
    resetScroll: true,
    hashScrollIntoView: {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    },
  })

  const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE
  const pageCount = Math.ceil(filteredBookmarkedProducts.length / itemsPerPage) || 1
  const currentPageItems = filteredBookmarkedProducts.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  )

  ;(function correctPage() {
    if (isFetching || !bookmarkedProducts) return
    if (page > pageCount) void setPage(pageCount)
  })()

  const handlePageChange = useCallback((event: {selected: number}) => {
    void setPage(event.selected + 1)
  }, [])

  const hrefBuilder = useCallback(
    (page: number) => {
      return router.buildLocation({
        to: BookmarkRoute.fullPath,
        search: {
          page,
        },
      }).href
    },
    [router],
  )

  return (
    <>
      <HeadTags title='Bookmarks' />
      <div className={style.Page}>
        <SkipLink title='Skip to main content' to='#main-content' />
        <Navbar variant='dark' />
        <main className={style.BookmarkPage} {...skipTargetProps('main-content')}>
          <div className={style.HeadingWrapper}>
            <h1 className={style.Heading}>
              Bookmarks ({filteredBookmarkedProducts.length.toString()})
              {isFetching && <LoadingIcon className={style.LoadingIcon} aria-hidden />}
            </h1>
            <form
              role='search'
              className={style.SearchWrapper}
              onSubmit={e => {
                e.preventDefault()
                announce(
                  `${filteredBookmarkedProducts.length.toString()} bookmarks found for "${
                    deferredQ
                  }"`,
                )
              }}
            >
              <input
                type='search'
                className={style.Search}
                value={q}
                onChange={e => {
                  setQ(e.currentTarget.value)
                }}
                placeholder='Search bookmarks...'
              />
              <button type='submit' className={style.SearchBtn} aria-label='Search'>
                <img src={searchSvg} alt='' />
              </button>
            </form>
          </div>
          {isLoading && (
            <VisuallyHidden className={style.Loading} role='progressbar'>
              Loading
            </VisuallyHidden>
          )}
          {bookmarkedProductsChunkify(currentPageItems).map(chunk => (
            <div key={chunk.text}>
              <div className={style.ChunkTitle} role='heading' aria-level={2}>
                {`Bookmarks from ${chunk.text}`}
              </div>
              <div className={style.Products}>
                {chunk.products.map(product => (
                  <div className={style.Product} key={`${product.retailer}-${product.id}`}>
                    <ProductCard product={product} headingLevel={3} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {pageCount > 1 && (
            <Pagination
              onPageChange={handlePageChange}
              pageCount={pageCount}
              currentPage={page - 1}
              hrefBuilder={hrefBuilder}
            />
          )}
        </main>
        <Footer />
      </div>
    </>
  )
})
